"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { field, input, label, leftIcon, rightIcon } from './styles.js';
import { renderComponentOrElement } from '../utils/helpers/component.js';
import { ContentText } from '../ContentText/ContentText.web.js';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.web.js';
import { useFocused } from '../utils/hooks/useFocused.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { ThemeProvider } from 'styled-components';
import { useSmartControlledEventHandler } from '../utils/hooks/useSmartControlledEventHandler.web.js';
import { inPixels } from '../utils/helpers/style.js';
import { useComponentVariant } from '../utils/hooks/useComponentVariant.js';

var _excluded = ["disabled", "rightIcon", "leftIcon", "onLeftIconClick", "onRightIconClick", "leftIconAriaLabel", "rightIconAriaLabel", "id", "inputRef", "name", "onChange", "onBlur", "onFocus", "onKeyDown", "onKeyUp", "label", "placeholder", "readOnly", "required", "value", "variant", "error", "errorMessage", "errorMessageProps", "autoComplete", "size", "borderStyle", "borderRadius", "labelStyle", "type", "defaultValue", "inputProps", "disableBottomBorderRadius", "disableBottomBorder", "transparent", "maxLength", "inputMode", "color"];
var InputField = function InputField(_ref) {
  var disabled = _ref.disabled,
    rightIcon$1 = _ref.rightIcon,
    leftIcon$1 = _ref.leftIcon,
    onLeftIconClick = _ref.onLeftIconClick,
    onRightIconClick = _ref.onRightIconClick,
    leftIconAriaLabel = _ref.leftIconAriaLabel,
    rightIconAriaLabel = _ref.rightIconAriaLabel,
    id = _ref.id,
    inputRef = _ref.inputRef,
    name = _ref.name,
    onChange = _ref.onChange,
    onBlurProp = _ref.onBlur,
    onFocusProp = _ref.onFocus,
    onKeyDown = _ref.onKeyDown,
    onKeyUp = _ref.onKeyUp,
    label$1 = _ref.label,
    placeholder = _ref.placeholder,
    readOnly = _ref.readOnly,
    required = _ref.required,
    valueProp = _ref.value,
    variantProp = _ref.variant,
    error = _ref.error,
    errorMessage = _ref.errorMessage,
    errorMessageProps = _ref.errorMessageProps,
    autoComplete = _ref.autoComplete,
    sizeProp = _ref.size,
    borderStyleProp = _ref.borderStyle,
    borderRadiusProp = _ref.borderRadius,
    _ref$labelStyle = _ref.labelStyle,
    labelStyle = _ref$labelStyle === undefined ? 'floating' : _ref$labelStyle,
    _ref$type = _ref.type,
    type = _ref$type === undefined ? 'text' : _ref$type,
    _ref$defaultValue = _ref.defaultValue,
    defaultValueProp = _ref$defaultValue === undefined ? '' : _ref$defaultValue,
    inputProps = _ref.inputProps,
    _ref$disableBottomBor = _ref.disableBottomBorderRadius,
    disableBottomBorderRadius = _ref$disableBottomBor === undefined ? false : _ref$disableBottomBor,
    _ref$disableBottomBor2 = _ref.disableBottomBorder,
    disableBottomBorder = _ref$disableBottomBor2 === undefined ? false : _ref$disableBottomBor2,
    _ref$transparent = _ref.transparent,
    transparent = _ref$transparent === undefined ? false : _ref$transparent,
    _ref$maxLength = _ref.maxLength,
    maxLength = _ref$maxLength === undefined ? 128 : _ref$maxLength,
    inputMode = _ref.inputMode,
    color = _ref.color,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useSmartControlledEv = useSmartControlledEventHandler(defaultValueProp, valueProp, onChange),
    _useSmartControlledEv2 = _slicedToArray(_useSmartControlledEv, 2),
    value = _useSmartControlledEv2[0],
    handleChange = _useSmartControlledEv2[1];
  var _useFocused = useFocused(false, onFocusProp, onBlurProp),
    onBlur = _useFocused.onBlur,
    onFocus = _useFocused.onFocus;
  var theme = useTheme();
  var rawTheme = theme.inputField.web;
  var variant = variantProp || theme.variant;
  var inputTheme = useComponentVariant(rawTheme, variant);
  var borderStyle = borderStyleProp || inputTheme.borderStyle;
  var size = variant === 'condensed' ? 's' : sizeProp || theme.preferredComponentSize;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(field, _extends({
    "data-testid": "elemental-InputField-".concat(id || name),
    $error: error,
    $errorColor: inputTheme.error,
    $disabled: disabled,
    $transparent: transparent,
    hidden: type === 'hidden'
  }, props), /*#__PURE__*/React.createElement(input, _extends({
    "data-testid": "elemental-InputField-".concat(id || name, "-input"),
    disabled: disabled,
    id: id || name,
    name: name,
    onChange: handleChange,
    onBlur: onBlur,
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    readOnly: readOnly,
    required: required,
    type: type,
    inputMode: inputMode,
    value: value,
    ref: inputRef,
    placeholder: labelStyle === 'floating' ? placeholder : label$1,
    $withLeftIcon: leftIcon$1 !== undefined,
    $withRightIcon: rightIcon$1 !== undefined,
    $labelStyle: labelStyle,
    $borderStyle: borderStyle,
    autoComplete: autoComplete,
    $color: color ? color : inputTheme.color,
    $borderRadius: borderRadiusProp !== null && borderRadiusProp !== undefined ? borderRadiusProp : theme.borders.radius,
    $variant: variant,
    $transparent: transparent,
    "aria-required": required,
    "aria-labelledby": "".concat(id || name, "__label"),
    "aria-invalid": error,
    "aria-describedby": error ? "elemental-InputField-".concat(id || name, "__error") : undefined,
    $disableBottomBorderRadius: borderStyle === 'bottomHidden' || borderStyle === 'bottomOnly' || disableBottomBorderRadius,
    $disableBottomBorder: disableBottomBorder,
    $fieldSize: size,
    maxLength: maxLength
  }, inputProps)), /*#__PURE__*/React.createElement(label, {
    "data-testid": "elemental-InputField-".concat(id || name, "-label"),
    id: "".concat(id || name, "__label"),
    htmlFor: id || name,
    $withLeftIcon: leftIcon$1 !== undefined,
    $withRightIcon: rightIcon$1 !== undefined,
    $readOnly: readOnly,
    $variant: variant,
    $fieldSize: size,
    $transparent: transparent
  }, required ? "".concat(label$1, " *") : label$1), leftIcon$1 && /*#__PURE__*/React.createElement(leftIcon, {
    "aria-hidden": onLeftIconClick ? false : true,
    tabIndex: onLeftIconClick ? 0 : -1,
    "data-testid": "elemental-InputField-".concat(id || name, "-leftIcon"),
    as: onLeftIconClick ? undefined : 'span',
    onClick: onLeftIconClick,
    type: onLeftIconClick ? 'button' : undefined,
    "aria-label": leftIconAriaLabel || label$1,
    $fieldSize: size,
    style: {
      outline: onLeftIconClick ? undefined : 'none',
      cursor: onLeftIconClick ? 'pointer' : undefined,
      pointerEvents: onLeftIconClick ? undefined : 'none'
    },
    $variant: variant
  }, renderComponentOrElement(leftIcon$1, {
    size: inPixels(inputTheme.sizings[size].icons.size)
  })), /*#__PURE__*/React.createElement(ErrorMessage, _extends({
    active: Boolean(error),
    id: "elemental-InputField-".concat(id || name, "__error"),
    textColor: transparent ? inputTheme.transparent.text : undefined
  }, errorMessageProps), /*#__PURE__*/React.createElement(ContentText, {
    size: variant === 'condensed' ? 's' : 'm'
  }, errorMessage || 'Please check input')), rightIcon$1 && /*#__PURE__*/React.createElement(rightIcon, {
    "aria-hidden": onRightIconClick ? false : true,
    tabIndex: onRightIconClick ? 0 : -1,
    "data-testid": "elemental-InputField-".concat(id || name, "-rightIcon"),
    onClick: onRightIconClick,
    as: onRightIconClick ? undefined : 'span',
    "aria-label": rightIconAriaLabel || label$1,
    type: onRightIconClick ? 'button' : undefined,
    $fieldSize: size,
    style: {
      outline: onRightIconClick ? undefined : 'none',
      cursor: onRightIconClick ? 'pointer' : undefined,
      pointerEvents: onRightIconClick ? undefined : 'none'
    },
    $variant: variant
  }, renderComponentOrElement(rightIcon$1, {
    size: inPixels(inputTheme.sizings[size].icons.size)
  }))));
};

export { InputField };
